/**
 * Custom size creator (round 6) — the "Custom size…" entry in the size
 * dropdown's Fine Art group. W × H inputs with an in / cm / mm unit switch,
 * live range validation against pricing.customSizing (converted to inches),
 * a paper picker limited to the non-specialty fine-art papers (never
 * C-Type), and a LIVE whole-dollar price via customPriceFor — the formula is
 * never shown. Create hands back sizeId = customSizeId(w, h) + the paper;
 * created sizes join the session recents so dropdowns list them for reuse.
 *
 * ROUND 9: opened with a photo in context, W × H PREFILL with that photo's
 * native print size at 300 DPI (px/300 inches, snapped to 0.1in, scaled —
 * aspect kept — into the custom range). Switching units converts the prefill
 * as long as the fields are untouched; anything the customer typed stays put.
 */

import { type PaperId, type PricingData } from '@rpl/pricing';
import { useMemo, useState } from 'react';
import {
  customPapers,
  customPrefill,
  customPriceSafe,
  customSizeIdFor,
  fromInches,
  snapInches,
  toInches,
  validateCustomInches,
  type SizeUnit,
} from '../../lib/customSize';
import { formatMoney } from '../../lib/format';
import { ModalShell } from './ModalShell';
import { Segmented } from './Segmented';

export interface CustomSizeDialogProps {
  pricing: PricingData;
  /** Paper to preselect (kept only if it is a valid custom-size paper). */
  initialPaper: PaperId;
  /** Photo in context — prefills W × H with its native print size at 300 DPI. */
  photo?: { width: number; height: number } | null;
  /** Create: apply this custom size + paper (dialog closes itself after). */
  onCreate: (sizeId: string, paper: PaperId) => void;
  onClose: () => void;
}

const UNITS: SizeUnit[] = ['in', 'cm', 'mm'];

export function CustomSizeDialog({ pricing, initialPaper, photo, onCreate, onClose }: CustomSizeDialogProps) {
  const papers = useMemo(() => customPapers(pricing), [pricing]);
  const [paper, setPaper] = useState<PaperId>(
    papers.includes(initialPaper) ? initialPaper : papers[0] ?? 'bauhaus-matt',
  );
  const [unit, setUnit] = useState<SizeUnit>('in');
  // Prefill from the photo's native print size (the initial unit is inches).
  const initial = useMemo(() => customPrefill(pricing, photo, 'in'), [pricing, photo]);
  const [wText, setWText] = useState(initial ? String(initial.w) : '');
  const [hText, setHText] = useState(initial ? String(initial.h) : '');

  /** Unit switch: convert the prefill while untouched; never touch typed values. */
  const switchUnit = (next: SizeUnit) => {
    const cur = customPrefill(pricing, photo, unit);
    const nxt = customPrefill(pricing, photo, next);
    if (cur && nxt && wText === String(cur.w) && hText === String(cur.h)) {
      setWText(String(nxt.w));
      setHText(String(nxt.h));
    }
    setUnit(next);
  };

  const w = Number(wText);
  const h = Number(hText);
  const entered = wText.trim() !== '' && hText.trim() !== '' && Number.isFinite(w) && Number.isFinite(h);
  const wIn = entered ? snapInches(toInches(w, unit)) : 0;
  const hIn = entered ? snapInches(toInches(h, unit)) : 0;
  const check = entered ? validateCustomInches(pricing, wIn, hIn) : { ok: false };
  const sizeId = check.ok ? customSizeIdFor(w, h, unit) : null;
  const price = sizeId ? customPriceSafe(pricing, sizeId, paper) : null;

  const cs = pricing.customSizing;
  const maxShort = Math.min(...cs.maxInches);
  const maxLong = Math.max(...cs.maxInches);
  const rangeHint =
    unit === 'in'
      ? `From ${cs.minInches}" on the short edge up to ${maxShort} × ${maxLong}"`
      : `From ${fromInches(cs.minInches, unit)} ${unit} on the short edge up to ${fromInches(maxShort, unit)} × ${fromInches(maxLong, unit)} ${unit}`;

  const create = () => {
    if (!sizeId || price == null) return;
    onCreate(sizeId, paper);
    onClose();
  };

  return (
    <ModalShell onClose={onClose} width={440} label="Create a custom size" className="cusize">
      <div className="cusize__head">
        <div>
          <div className="cusize__title">Custom size</div>
          <div className="cusize__sub">Fine art prints, made to your exact size.</div>
        </div>
        <button type="button" className="cusize__close" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </div>

      <div className="cusize__body">
        <div className="cusize__dims">
          <label className="cusize__field">
            <span className="eyebrow cusize__label">Width</span>
            <input
              className="cusize__input"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.1"
              value={wText}
              onChange={(e) => setWText(e.target.value)}
              aria-label={`Width in ${unit}`}
            />
          </label>
          <span className="cusize__times" aria-hidden>
            ×
          </span>
          <label className="cusize__field">
            <span className="eyebrow cusize__label">Height</span>
            <input
              className="cusize__input"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.1"
              value={hText}
              onChange={(e) => setHText(e.target.value)}
              aria-label={`Height in ${unit}`}
            />
          </label>
          <Segmented
            variant="light"
            className="cusize__units"
            ariaLabel="Units"
            segments={UNITS.map((u) => ({ key: u, label: u }))}
            value={unit}
            onChange={(key) => switchUnit(key as SizeUnit)}
          />
        </div>
        <div className="cusize__hint">{rangeHint}</div>
        {entered && !check.ok && 'error' in check && check.error && (
          <div className="cusize__error" role="alert">
            {check.error}
          </div>
        )}
        {check.ok && unit !== 'in' && (
          <div className="cusize__converted">
            Prints at {wIn} × {hIn} inches
          </div>
        )}

        <div className="cusize__papers">
          <span className="eyebrow cusize__label">Paper</span>
          <div className="cusize__paper-list" role="radiogroup" aria-label="Fine art paper">
            {papers.map((p) => {
              const info = pricing.papers[p];
              const pPrice = sizeId ? customPriceSafe(pricing, sizeId, p) : null;
              return (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={p === paper}
                  className={`cusize__paper${p === paper ? ' cusize__paper--on' : ''}`}
                  onClick={() => setPaper(p)}
                >
                  <span className="cusize__paper-name">
                    {info?.label ?? p}
                    {info?.badge && <span className="dd__badge">{info.badge}</span>}
                  </span>
                  {pPrice != null && <span className="cusize__paper-price">{formatMoney(pPrice)}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="cusize__pricing">
          <div className="cusize__price-label">Price</div>
          <div className="cusize__price">{price != null ? formatMoney(price) : '—'}</div>
          <div className="cusize__price-note">Whole-dollar pricing. GST included.</div>
        </div>
      </div>

      <div className="cusize__foot">
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn--primary" disabled={!sizeId || price == null} onClick={create}>
          Use this size
        </button>
      </div>
    </ModalShell>
  );
}
